import { useEffect, useRef, useState } from 'react';
import PlanFreshnessAsk from './PlanFreshnessAsk.jsx';
import { shouldAskPlanFreshness } from '../../utils/planFreshness.js';
import {
  PLANNER_DAY_LONG,
  defaultPlannerCalendarContext,
} from '../../utils/mealPlanHelpers.js';
import { scrollElementToScrollRootTop } from '../../utils/scrollElementToScrollRootTop.js';
import PlannerWhySuffix from './PlannerWhySuffix.jsx';
import MealSlotStatusBadge from './MealSlotStatusBadge.jsx';
import MealRowActionsMenu from './MealRowActionsMenu.jsx';
import PastNightResolveRow from './PastNightResolveRow.jsx';
import { isNightOffMeal } from '../../utils/nightOff.js';
import './planner.css';

function snapshotRect(el) {
  if (!el?.getBoundingClientRect) return null;
  const rect = el.getBoundingClientRect();
  return {
    top: rect.top,
    bottom: rect.bottom,
    left: rect.left,
    right: rect.right,
    width: rect.width,
    height: rect.height,
  };
}

/** Pin the meal card to the top of `.main-content`, then capture its viewport rect. */
function anchorMealForMoveSheet(el) {
  scrollElementToScrollRootTop(el);
  return snapshotRect(el);
}

/*
 * Mobile meal card — single component, two states (assigned / unassigned).
 *
 * Props (assigned):
 *   meal, state='assigned', onOpenMoveSheet, onUnassign
 * Props (unassigned):
 *   meal, state='unassigned', onOpenMoveSheet, canAssign
 *   dayDates: optional Record<dayKey, number> for calendar labels (live plan week)
 *   onRemovePick: optional () => void — unpin from Your Picks
 *   removePickBusy: boolean
 *   isUserPick: boolean — Cook-pinned candidate (visual accent in unified pool)
 */
export default function MobileMealCard({
  meal,
  state,
  onOpenMoveSheet,
  onUnassign,
  onSwap,
  onRemovePick,
  removePickBusy = false,
  isUserPick = false,
  isHighlighted = false,
  onViewRecipe,
  onMarkCooked,
  onMarkSkipped,
  onNightOff,
  canAssign = true,
  dayDates = null,
  isToday = false,
}) {
  const cardRef = useRef(null);
  const [freshnessSettled, setFreshnessSettled] = useState(false);
  useEffect(() => {
    setFreshnessSettled(false);
  }, [meal.planItemId, meal.recipeId, meal.agingItem?.id]);
  const showFreshness = !freshnessSettled && shouldAskPlanFreshness(meal);
  const isAssigned = state === 'assigned';
  const isPast = isAssigned && !!meal.isPast;
  const cookable   = meal.have >= meal.need;
  const shopCount  = meal.need - meal.have;
  const dates = dayDates ?? defaultPlannerCalendarContext().dayDates;
  const showPastActions =
    isPast &&
    meal.status === 'planned' &&
    meal.recipeId &&
    Boolean(onMarkCooked || onMarkSkipped || onNightOff);
  const nightOff = isAssigned && isNightOffMeal(meal);

  const openMoveSheet = () => {
    if (isPast) return;
    onOpenMoveSheet?.(meal, anchorMealForMoveSheet(cardRef.current));
  };

  const unassignedAriaLabel = !isAssigned
    ? (isUserPick ? `Your pick: ${meal.name}` : `Suggested: ${meal.name}`)
    : undefined;

  if (isAssigned && showPastActions) {
    return (
      <PastNightResolveRow
        meal={meal}
        dayDates={dates}
        isToday={isToday}
        onMarkCooked={onMarkCooked}
        onMarkSkipped={onMarkSkipped}
        onNightOff={onNightOff}
      />
    );
  }

  return (
    <div
      ref={cardRef}
      className={[
        'mobile-meal-card',
        !isAssigned && !canAssign ? 'disabled' : '',
        isAssigned ? 'assigned' : '',
        isPast ? 'past' : '',
        nightOff ? 'night-off' : '',
        isAssigned && isToday ? 'mobile-meal-card--today' : '',
        !isAssigned && isUserPick ? 'mobile-meal-card--user-pick' : '',
        isHighlighted ? 'mobile-meal-card--plan-highlight' : '',
      ].filter(Boolean).join(' ')}
      aria-label={unassignedAriaLabel}
      data-plan-highlight={isHighlighted ? 'true' : undefined}
    >
      {meal.repeat && !isPast && (
        <div className="mobile-repeat-badge">
          {isAssigned ? 'Repeat' : 'Cooked recently'}
        </div>
      )}

      <div className="mobile-meal-card-row">
        {isAssigned && meal.assignedTo && (
          <div
            className="mobile-meal-day-btn"
            aria-label={
              isPast
                ? `Assigned to ${PLANNER_DAY_LONG[meal.assignedTo]} (past — locked)`
                : `Assigned to ${PLANNER_DAY_LONG[meal.assignedTo]}`
            }
          >
            <div
              className="mobile-meal-day-bar"
              style={nightOff ? undefined : { background: meal.swatch }}
            />
            <div className="mobile-meal-day-stack">
              <div className="mobile-meal-day-letter" aria-hidden="true">
                {PLANNER_DAY_LONG[meal.assignedTo]}
              </div>
              <div className="mobile-meal-day-date">{dates[meal.assignedTo]}</div>
            </div>
            {isToday && <div className="meal-row-today-dot" aria-hidden="true" />}
          </div>
        )}

        {!isAssigned && !isUserPick && (
          <div
            className="mobile-meal-swatch"
            style={{ background: meal.swatch }}
            aria-hidden="true"
          />
        )}

        <div
          className={`mobile-meal-info${meal.repeat && !isPast ? ' mobile-meal-info--repeat' : ''}`}
        >
          <div className="mobile-meal-name mobile-meal-name-row">
            {nightOff ? (
              <MealSlotStatusBadge meal={meal} />
            ) : (
              <>
                <span>{meal.name}</span>
                {isAssigned && <MealSlotStatusBadge meal={meal} />}
              </>
            )}
          </div>
          {!isPast && !nightOff && (
            <div className="mobile-meal-sub">
              {isAssigned ? (
              <>
                {showFreshness ? null : cookable ? (
                  <span className="mobile-meal-status-cookable">Cookable now</span>
                ) : (
                  <span className="mobile-meal-status-shop">{shopCount} to shop</span>
                )}
                {showFreshness && cookable ? null : (
                  <>
                    {' · '}
                    {meal.time}
                  </>
                )}
                <PlannerWhySuffix meal={meal} variant="mobile" />
              </>
            ) : (
              <>
                <span className={isUserPick ? 'mobile-meal-source-match' : meal.source === 'match' ? 'mobile-meal-source-match' : 'mobile-meal-source-added'}>
                  {isUserPick ? 'Your pick' : meal.source === 'match' ? 'Pantry match' : 'From Recipes'}
                </span>
                {' · '}{meal.have}/{meal.need}
                <PlannerWhySuffix meal={meal} variant="mobile" />
              </>
              )}
            </div>
          )}
        </div>

        {!isAssigned && (
          <button
            type="button"
            className="mobile-add-btn"
            onClick={openMoveSheet}
            disabled={!canAssign}
            aria-label={
              canAssign
                ? `Choose a night for ${meal.name}`
                : 'No open nights available'
            }
          >
            +
          </button>
        )}
      </div>

      {showFreshness && (
        <PlanFreshnessAsk meal={meal} onSettled={() => setFreshnessSettled(true)} />
      )}

      {isAssigned && !isPast && (
        <div className="mobile-pick-remove-row">
          {onViewRecipe && !nightOff && (
            <button
              type="button"
              className="planner-pick-remove-link planner-recipe-details-link"
              onClick={(e) => {
                e.stopPropagation();
                onViewRecipe();
              }}
            >
              Recipe Details →
            </button>
          )}
          <MealRowActionsMenu
            nightOff={nightOff}
            onMove={openMoveSheet}
            onSwap={() => onSwap?.(meal)}
            onRemove={onUnassign}
          />
        </div>
      )}

      {!isAssigned && onRemovePick && (
        <div className="mobile-pick-remove-row">
          <button
            type="button"
            className="planner-pick-remove-link"
            disabled={removePickBusy}
            onClick={onRemovePick}
          >
            Remove
          </button>
        </div>
      )}
    </div>
  );
}
